import * as React from 'react';
import moment, { Moment } from 'moment';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import classNames from 'classnames';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { getEventNameForParent, getEventType } from 'Src/helpers/accessor/accessor';
import { EventInvitationMessage } from 'Src/models/message';
import { SchoolEvent } from 'Src/models/event';
import { getSchoolEventLocation } from 'Src/helpers/event/schoolEventViewHelper';
import { isClubEvent } from 'Src/helpers/event/event';
import './AvailabilityReportCard.scss';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { INVITE_TYPE } from 'Src/consts/invite';

interface Props {
  item: EventInvitationMessage;
  type: string;
  childIdList?: string[];
  childEvent?: SchoolEvent;
  onViewMapClick: (item: EventInvitationMessage) => void;
  user: AppUser;
  onShowDetailsClick?: (item: EventInvitationMessage) => void;
  onRespondClick?: (item: EventInvitationMessage) => void;
}

export const AvailabilityReportCard: React.FC<Props> = ({ item, type, childIdList, childEvent, onViewMapClick, onShowDetailsClick, onRespondClick, user }) => {
  const studentName = getFullName(propz.get(item, ['playerDetailsData']));
  const initials = (studentName || '')
    .split(' ')
    .filter(Boolean)
    .map(s => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const schoolLogo = propz.get(item, ['schoolData', 'pic'], '');

  const ev = (childEvent || (propz.get(item, ['eventData']) as SchoolEvent) || {}) as SchoolEvent;
  const sportName = propz.get(ev, ['sport', 'name'], '') || propz.get(item, ['eventData', 'sport', 'name'], '');
  const schoolName = propz.get(item, ['schoolData', 'name'], '');
  const eventType = getEventType(ev);
  const genderRaw = propz.get(ev, ['gender'], '');
  const gender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[genderRaw] || String(genderRaw || '').replace(/_/g, ' ');
  const ages = formatAgesCompactForSchool(
    propz.get(ev, ['ages'], []),
    propz.get(item, ['schoolData'], user.activeSchool)
  );

  const eventName =
    (childEvent && Array.isArray(childIdList) && childIdList.length
      ? getEventNameForParent(childEvent, childIdList)
      : undefined) ||
    propz.get(ev, ['name'], '') ||
    propz.get(ev, ['title'], '') ||
    propz.get(ev, ['generatedNames', user.activeSchoolId], '') ||
    propz.get(ev, ['generatedNames', 'official'], '') ||
    (sportName ? `${sportName} fixture` : 'This event');

  const eventLocation = getSchoolEventLocation(ev, user);

  const coordinates = propz.get(ev, ['venue', 'postcodeData', 'point', 'coordinates']);
  const isCoordinatesExist = Array.isArray(coordinates) && coordinates.length === 2;

  const eventSpecificLocation = propz.get(ev, ['location'], '');
  const isExistEventSpecificLocation = eventSpecificLocation !== '';

  const startIso = propz.get(ev, ['startTime'], '') || propz.get(item, ['eventData', 'startTime'], '');
  const endIso =
    propz.get(ev, ['endTime'], '') ||
    propz.get(ev, ['finishTime'], '') ||
    propz.get(item, ['eventData', 'endTime'], '') ||
    propz.get(item, ['eventData', 'finishTime'], '');

  const start = moment(startIso);
  const end = moment(endIso);

  const hasStart = start.isValid();
  const hasEnd = end.isValid();
  const sameDay = hasStart && hasEnd && start.isSame(end, 'day');

  const prettyDate = (m: Moment) => m.format('ddd D MMM YYYY');
  const prettyTime = (m: Moment) => m.format('HH:mm');

  const dateLineSameDay = hasStart ? prettyDate(start) : '';
  const timeRangeSameDay =
    hasStart && hasEnd ? `${prettyTime(start)} – ${prettyTime(end)}` : hasStart ? prettyTime(start) : '';

  const dateRangeMulti =
    hasStart && hasEnd ? `${prettyDate(start)} – ${prettyDate(end)}` : hasStart ? prettyDate(start) : '';
  const timeRangeMulti =
    hasStart && hasEnd ? `${prettyTime(start)} – ${prettyTime(end)}` : hasStart ? prettyTime(start) : '';

  const availabilityValue = propz.get(item, ['isTakePart']);
  const isPending = typeof availabilityValue !== 'boolean';
  const responseCutoff = moment(propz.get(item, ['availabilityDeadline'], propz.get(item, ['eventData', 'startTime'], '')));
  const isExpired = isPending && responseCutoff.isValid() && moment().isSameOrAfter(responseCutoff);
  const isTakePart = availabilityValue === true;
  const details = propz.get(item, ['details'], '');

  const senderName =
    propz.get(item, ['sender', 'fullName']) ||
    [propz.get(item, ['sender', 'firstName']), propz.get(item, ['sender', 'lastName'])].filter(Boolean).join(' ') ||
    'Unknown';
  const isSentByStudent = String(propz.get(item, ['sender', 'userId'], '')) === String(propz.get(item, ['playerDetails', 'userId'], ''));
  const reportedAt = moment(propz.get(item, ['createdAt'], ''));

  const cardClasses = classNames('availabilityCard', 'bAvailabilityReportCard');

  return (
    <article className={cardClasses}>
      <header className="availabilityCard__header">
        <div className="ConsentCard__person">
          <div className="ConsentCard__avatar" aria-hidden="true">
            {schoolLogo ? <img src={schoolLogo} alt="" /> : initials || '•'}
          </div>
          <div className="ConsentCard__who">
            <div className="ConsentCard__student">{studentName}</div>
            <div className={`ConsentCard__status ${isExpired ? 'is-expired' : isPending ? 'is-pending' : isTakePart ? 'is-accepted' : 'is-declined'}`}>{isExpired ? 'Expired' : isPending ? 'Response needed' : isTakePart ? 'Available' : 'Not available'}</div>
          </div>
        </div>
      </header>
        <h3 className="availabilityCard__title clamp-2" title={eventName}>
          {eventName}
        </h3>

      <div className="availabilityCard__chips">
        {sportName ? <span className="is-sport">{sportName}</span> : null}
        {schoolName ? <span className="is-school">{schoolName}</span> : null}
        {eventType ? <span>{eventType}</span> : null}
        {gender ? <span>{gender}</span> : null}
        {ages ? <span>{ages}</span> : null}
      </div>

      <div className="availabilityCard__location">
        <div className="ConsentCard__venue">
          <img className="ConsentCard__pin" src="/dist/images/icons/location.png" alt="" width={18} height={18} />

          {eventLocation ? (
            isCoordinatesExist ? (
              <a
                href="#"
                className="ConsentCard__venueLink ConsentCard__venueText"
                onClick={e => {
                  e.preventDefault();
                  onViewMapClick && onViewMapClick(item);
                }}
                title="Open map"
              >
                {eventLocation}
              </a>
            ) : (
              <span className="ConsentCard__venueText">{eventLocation}</span>
            )
          ) : (
            <span className="ConsentCard__venueLink--muted">No venue info</span>
          )}
        </div>

        {isExistEventSpecificLocation && (
          <div className="ConsentCard__venueHint" title={eventSpecificLocation}>
            Specific location: {eventSpecificLocation}
          </div>
        )}
      </div>

      <div className="availabilityCard__date">
        {hasStart ? (
          sameDay && hasEnd ? (
            <div className="ConsentCard__datePill" title={`${dateLineSameDay} • ${timeRangeSameDay}`}>
              <span className="ConsentCard__date">{dateLineSameDay}</span>
              <span className="ConsentCard__sep" aria-hidden="true">
                •
              </span>
              <span className="ConsentCard__time">{timeRangeSameDay}</span>
            </div>
          ) : hasEnd ? (
            <div className="ConsentCard__dateRange">
              <div className="ConsentCard__date">{dateRangeMulti}</div>
              <div className="ConsentCard__time">{timeRangeMulti}</div>
            </div>
          ) : (
            <div className="ConsentCard__datePill" title={`${prettyDate(start)} • ${prettyTime(start)}`}>
              <span className="ConsentCard__date">{prettyDate(start)}</span>
              <span className="ConsentCard__sep" aria-hidden="true">
                •
              </span>
              <span className="ConsentCard__time">{prettyTime(start)}</span>
            </div>
          )
        ) : (
          <div className="ConsentCard__dateRange">
            <div className="ConsentCard__date">Date TBC</div>
          </div>
        )}
      </div>

      <div className="availabilityCard__sender">
        <div
          className="bAvailabilityReportCard__sender"
          role="note"
          aria-label={isPending ? `Requested by ${senderName}` : `Reported by ${isSentByStudent ? 'student' : 'parent'} ${senderName}`}
          title={isPending ? `Requested by ${senderName}` : `Reported by ${isSentByStudent ? 'student' : 'parent'} ${senderName}`}
        >
          <span className="bAvailabilityReportCard__senderLabel">{isPending ? 'Requested by' : `Reported by ${isSentByStudent ? 'student' : 'parent'}`} · </span>
          <span className="bAvailabilityReportCard__senderValue">{senderName}</span>
          {reportedAt.isValid() ? (
            <time dateTime={reportedAt.toISOString()}> · {reportedAt.format('D MMM YYYY, HH:mm')}</time>
          ) : null}
        </div>
      </div>

      {details && (
        <div className="availabilityCard__noteWrap">
          <div className="bAvailabilityReportCard__note" title={details}>
            <div className="bAvailabilityReportCard__noteBody clamp-3">{details}</div>
          </div>
        </div>
      )}
      {onRespondClick && type !== INVITE_TYPE.ARCHIVE ? <footer className="availabilityCard__footer"><button type="button" className="is-primary" onClick={() => onRespondClick(item)}>{isPending ? 'Respond' : 'Update response'}</button></footer> : type === INVITE_TYPE.ARCHIVE && onShowDetailsClick ? <footer className="availabilityCard__footer"><button type="button" onClick={() => onShowDetailsClick(item)}>View details</button></footer> : null}
    </article>
  );
};

AvailabilityReportCard.displayName = 'AvailabilityReportCard';



